import React, { useState } from "react";
import {
  Alert,
  Form,
  Button,
  Spin,
  Result,
  notification,
  Card,
  Space,
  message,
} from "antd";
import omit from "lodash/omit";
import "./App.css";
import Patient from "./Patient";
import Examination from "./Examination";
import Results from "./Results";
import useSWR from "swr";
import isNil from "lodash/isNil";
import moment from "moment";
import { questionnaireFetcher } from "./api";

async function createObservation(values, components, questionnaire) {
  const payload = {
    resourceType: "Observation",
    status: "registered",
    code: {
      coding: questionnaire.code,
    },
    specimen: { reference: `Specimen/${values.specimenId}` },
    subject: { reference: `Patient/${values.patientId}` },
    performer: [{ reference: `Practitioner/${values.practitionerId}` }],
    effectiveDateTime: moment().format(),
    component: questionnaire.questions
      .filter((item) => !isNil(components[item.id]))
      .map((item) => ({
        code: {
          coding: item.code,
        },
        valueQuantity: {
          value: components[item.id],
          unit: item.unit.code,
          code: item.unit.code,
          system: item.unit.system,
        },
      })),
  };

  return fetch(
    "https://servers.techconnect.cl/HAPI/Observation",
    {
      method: "POST",
      mode: "cors",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    }
  ).then((res) => res.json());
}

function App() {
  const [form] = Form.useForm();
  const [questionnaireCode, setQuestionnaireCode] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(false);
  const [observationId, setObservationId] = useState(null);
  const { data: questionnaire } = useSWR(
    questionnaireCode,
    questionnaireFetcher
  );

  const onSubmit = (values) => {
    setSubmitting(true);

    const fields = [
      "patientId",
      "practitionerId",
      "questionnaireCode",
      "date",
      "specimenId",
    ];

    const components = omit(values, [
      ...fields,
      "patientRun",
      "practitionerRun",
    ]);

    const fieldValues = form.getFieldsValue(fields);

    createObservation(fieldValues, components, questionnaire)
      .then((observation) => {
        setObservationId(observation.id);
        setCreated(true);
      })
      .catch(() => {
        notification.error({
          message: "Ocurrió un errror al enviar el resultado.",
          description: "Por favor, vuelve a intentarlo",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleValuesChange = (newValues) => {
    if (newValues.questionnaireCode) {
      setQuestionnaireCode(newValues.questionnaireCode);
    }
  };

  const handlePatientChange = (patient) => {
    form.setFieldsValue({ patientId: patient.id });
    setPatientId(patient.id);
  };

  const handlePractitionerChange = (practitioner) => {
    form.setFieldsValue({ practitionerId: practitioner.id });
  };

  if (created) {
    return (
      <Result
        status="success"
        title="Ingreso exitoso"
        subTitle={`Id de referencia: ${observationId}`}
      />
    );
  }

  return (
    <Spin tip="Guardando resultado de examen..." spinning={submitting}>
      <Form form={form} onValuesChange={handleValuesChange} onFinish={onSubmit}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Card title="Información del paciente">
            <Patient
              onError={(run) =>
                message.warning(`No encontramos al paciente con RUN '${run}'`)
              }
              onChange={handlePatientChange}
            />
          </Card>
          <Card title="Registro del examen">
            <Examination
              questionnaireCode={questionnaireCode}
              patientId={patientId}
              onPractitionerChange={handlePractitionerChange}
            />
          </Card>
          <Card title="Resultados Examen">
            {questionnaire ? (
              <Results questions={questionnaire.questions} />
            ) : (
              <Alert
                message="Selecciona 'Perfil Lipídico' o 'Perfil Hepático' antes de ingresar los resultados"
                type="info"
                showIcon
              />
            )}
          </Card>
          <Form.Item>
            <Button htmlType="submit" size="large" type="primary">
              Enviar
            </Button>
          </Form.Item>
        </Space>
      </Form>
    </Spin>
  );
}

export default App;
